import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { InstanceHostSelection } from "../host_selection/Instance";
import { HostedMeetingFallbackSection } from "../meeting_details/HostedMeetingFallbackSection";
import {
  PartnerAssociationSection,
  PartnerAssociationHandle,
} from "../meeting_details/PartnerAssociationSection";
import { Meeting } from "../../../../model/meeting";
import { useBoundStore } from "../../../../store/store";
import { Host } from "../../../../model/host";
import { MeetingTypeInfoSelect } from "../MeetingTypeInfoSelect";
import { ContinuousRoomKeyField } from "./ContinuousRoomKeyField";
import { ApplyScope, ApplyScopeDialog } from "./ApplyScopeDialog";
import { MeetingHostSchedule } from "../../../../model/meeting_host_schedule";
import { useIsMobile } from "../../../../hooks/useIsMobile";

const withoutRoomKey = (meeting: Meeting): Meeting => ({
  ...meeting,
  metadata: { ...meeting.metadata, continuousRoomKey: "" },
});

interface Props {
  meeting: Meeting;
  open: boolean;
  onClose: () => void;
}

export const InstanceMetadataEditDialog: React.FC<Props> = ({
  meeting: originalMeeting,
  open,
  onClose,
}) => {
  const setInstanceMetadata = useBoundStore(
    (state) => state.setInstanceMetadata,
  );
  const setRecurringMetadata = useBoundStore(
    (state) => state.setRecurringMetadata,
  );
  // A partner-private meeting keeps no room key (the backend clears it and
  // the calendar sync keeps it out of rooms), so the form never shows one.
  const privateAtLoad = !!originalMeeting.metadata.partnerId?.trim();
  const [partnerOnly, setPartnerOnly] = useState(privateAtLoad);
  const [currentMeeting, setCurrentMeeting] = useState(() =>
    privateAtLoad ? withoutRoomKey(originalMeeting) : originalMeeting,
  );
  // Whether the key field holds a key the admin typed, rather than the one
  // loaded (or the blank a private meeting shows).
  const [keyEdited, setKeyEdited] = useState(false);
  // The key field as it was when Partner-only was switched on, so switching it
  // back off returns the form to exactly that state.
  const beforePrivate = React.useRef<{
    key?: string;
    edited: boolean;
  } | null>(null);
  const [scopePromptOpen, setScopePromptOpen] = useState(false);
  const isMobile = useIsMobile();

  const recurringMeeting = originalMeeting.recurringMeeting;
  const isRecurring = recurringMeeting !== undefined;
  const partnerRef = React.useRef<PartnerAssociationHandle>(null);

  const applyChanges = React.useCallback(
    async (scope: ApplyScope) => {
      // Validate the partner section BEFORE persisting anything — a bad
      // selection there must not leave the metadata half-saved.
      partnerRef.current?.validate();

      // Partner scope follows the chosen scope: this occurrence only, or the
      // whole series (the backend also rewrites occurrences edited on their
      // own). "" clears — see InstanceMetadata.partnerId. Until the admin
      // changes it (and while the section is still loading) nothing is sent
      // for the series, so each occurrence keeps the scope it has.
      const scopeChanged = partnerRef.current?.partnerScopeChanged() ?? false;
      const partnerId = scopeChanged
        ? (partnerRef.current?.desiredPartnerId() ?? "")
        : (originalMeeting.metadata.partnerId?.trim() ?? "");
      const appliesToSeries =
        scope === ApplyScope.Recurring && recurringMeeting !== undefined;

      try {
        if (appliesToSeries && recurringMeeting) {
          // "All events" — push form values up to the master. Per-day host
          // overrides on the series are kept; only `default` is updated when
          // the form has a host. Use the Edit Recurrence dialog for full
          // schedule editing.
          const existingSchedule =
            recurringMeeting.metadata.hostSchedule ??
            ({} as MeetingHostSchedule);
          const nextSchedule: MeetingHostSchedule | undefined = currentMeeting
            .metadata.host
            ? { ...existingSchedule, default: currentMeeting.metadata.host }
            : recurringMeeting.metadata.hostSchedule;

          // The key goes up only when it is a real one for a series that stays
          // public: a private occurrence shows a blank that must not wipe the
          // series' key, and a private series keeps none.
          const seriesPartnerId = scopeChanged
            ? partnerId
            : (recurringMeeting.metadata.partnerId?.trim() ?? "");
          const sendKey =
            !seriesPartnerId && !partnerId && (!privateAtLoad || keyEdited);
          await setRecurringMetadata(recurringMeeting.id, {
            meetingType: currentMeeting.metadata.meetingType,
            memberVisible: currentMeeting.metadata.memberVisible,
            hostSchedule: nextSchedule,
            ...(sendKey && {
              continuousRoomKey: currentMeeting.metadata.continuousRoomKey,
            }),
            // Only when changed: sending it rewrites every occurrence, which
            // must not happen on a save that was only about the host or type.
            ...(scopeChanged && { partnerId }),
          });
        } else {
          await setInstanceMetadata(currentMeeting.id, {
            ...currentMeeting.metadata,
            partnerId,
          });
        }
      } catch (error) {
        // Every other failure path already shows its own message; without
        // this a failed write would leave the dialog open and silent.
        partnerRef.current?.reportError(
          "Failed to save the meeting. Try again.",
        );
        throw error;
      }
      // Persist the partner association under the same Save (throws → the
      // dialog stays open and the section shows an error toast).
      await partnerRef.current?.save();
      onClose();
    },
    [
      currentMeeting,
      originalMeeting,
      privateAtLoad,
      keyEdited,
      recurringMeeting,
      setInstanceMetadata,
      setRecurringMetadata,
      onClose,
    ],
  );

  // A rejected save has already shown its message; it only has to keep the
  // dialog open, not escape as an unhandled rejection.
  const keepDialogOpen = () => undefined;

  const handleSave = React.useCallback(async () => {
    if (isRecurring) {
      setScopePromptOpen(true);
      return;
    }
    await applyChanges(ApplyScope.Instance).catch(keepDialogOpen);
  }, [isRecurring, applyChanges]);

  const handleScopeChosen = React.useCallback(
    async (scope: ApplyScope) => {
      setScopePromptOpen(false);
      await applyChanges(scope).catch(keepDialogOpen);
    },
    [applyChanges],
  );

  return (
    <>
      <Dialog
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        open={open}
        onClose={onClose}
      >
        <DialogTitle>
          <Stack gap={0.5}>
            {"Editing "}
            {currentMeeting.title}
            {isRecurring && (
              <Typography variant="caption">
                You're editing a single instance of a recurring meeting. On
                save, you'll be asked whether to apply changes to this event
                only or to the entire series.
              </Typography>
            )}
          </Stack>
        </DialogTitle>

        <DialogContent>
          <InstanceHostSelection
            value={currentMeeting.metadata.host}
            onChange={(value: Host) => {
              setCurrentMeeting({
                ...currentMeeting,
                metadata: { ...currentMeeting.metadata, host: value },
              });
            }}
          />

          <MeetingTypeInfoSelect
            currentMeetingTypeId={currentMeeting.metadata.meetingType}
            onMeetingTypeChange={(meetingTypeId: string) => {
              setCurrentMeeting({
                ...currentMeeting,
                metadata: {
                  ...currentMeeting.metadata,
                  meetingType: meetingTypeId,
                },
              });
            }}
            formControlProps={{ fullWidth: true, margin: "normal" }}
            selectProps={{ label: "Meeting Type" }}
          />

          <ContinuousRoomKeyField
            disabled={partnerOnly}
            value={currentMeeting.metadata.continuousRoomKey}
            onChange={(value: string) => {
              setKeyEdited(true);
              setCurrentMeeting({
                ...currentMeeting,
                metadata: {
                  ...currentMeeting.metadata,
                  continuousRoomKey: value,
                },
              });
            }}
          />

          <FormControl fullWidth margin="normal">
            <FormControlLabel
              control={
                <Switch
                  checked={currentMeeting.metadata.memberVisible ?? false}
                  onChange={() =>
                    setCurrentMeeting({
                      ...currentMeeting,
                      metadata: {
                        ...currentMeeting.metadata,
                        memberVisible: !currentMeeting.metadata.memberVisible,
                      },
                    })
                  }
                />
              }
              label="Visible to Members"
            />
            <FormHelperText>
              When checked, members are able to see and subscribe to this
              meeting
            </FormHelperText>
          </FormControl>

          <HostedMeetingFallbackSection gcalEventId={currentMeeting.id} />

          <PartnerAssociationSection
            ref={partnerRef}
            gcalEventId={currentMeeting.id}
            recurringMeetingId={recurringMeeting?.id}
            partnerId={currentMeeting.metadata.partnerId}
            onPartnerOnlyChange={(on) => {
              setPartnerOnly(on);
              if (on) {
                beforePrivate.current = {
                  key: currentMeeting.metadata.continuousRoomKey,
                  edited: keyEdited,
                };
                setCurrentMeeting(withoutRoomKey);
                setKeyEdited(false);
                return;
              }
              // Nothing saved means it was not switched on here: either it
              // loaded private (the blank stays, so the room-key rules apply
              // again) or this "off" comes from turning Preferred on while it
              // was already off (the key must not change).
              const saved = beforePrivate.current;
              if (!saved) return;
              beforePrivate.current = null;
              setCurrentMeeting((m) => ({
                ...m,
                metadata: { ...m.metadata, continuousRoomKey: saved.key },
              }));
              setKeyEdited(saved.edited);
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ApplyScopeDialog
        open={scopePromptOpen}
        onCancel={() => setScopePromptOpen(false)}
        onChoose={handleScopeChosen}
      />
    </>
  );
};
